import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button } from '../../components';
import { cx } from '../../lib/cx';
import { formatDate, formatHours } from '../../lib/format';
import type { BacklogRow } from '../backlog/api';
import { useCapability } from '../workspace/useCapability';
import { useVendorName } from '../workspace/useWorkspace';
import {
  decidePlanChange,
  invalidatePlanning,
  raisePlanChange,
  type PlanChange,
  type PlanChangeItem,
} from './api';
import { SectionLabel } from './parts';

import type { PlanColumn } from '../plan/usePlanBoard';

/**
 * Change of plan — v0.3 §6.4 rules 4 and 5, on the sprint it changes.
 *
 * Rule 4: the Client Owner picks what to add and what to take out (or accepts
 * the sprint may run over), and gives a reason. It goes to the Project Lead,
 * who agrees and applies it. Rule 5: the sprint page shows a plan-changes count
 * and history, "so it's clear how often scope moved and why".
 *
 * The history is the point. Before this, "the sprint has been moved forward due
 * to enhanced scope" was a note in a spreadsheet cell; here every move says who
 * asked, what came in and went out, why, who agreed, and the effect on hours.
 */
export function ChangeOfPlan({
  workspaceId,
  timeline,
  backlog,
}: {
  workspaceId: string;
  timeline: PlanColumn;
  backlog: readonly BacklogRow[];
}) {
  const canRaise = useCapability('changeofplan.raise');
  const canDecide = useCapability('planning.write');
  const [open, setOpen] = useState(false);
  const changes = timeline.planChanges;

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <SectionLabel>Plan changes · {changes.length}</SectionLabel>
        {canRaise && !open && (
          <Button size="sm" onClick={() => setOpen(true)}>
            Request change of plan
          </Button>
        )}
      </div>

      {open && (
        <RaiseForm
          workspaceId={workspaceId}
          timeline={timeline}
          backlog={backlog}
          onDone={() => setOpen(false)}
        />
      )}

      {changes.length === 0 ? (
        !open && <p className="fs-12 text-muted">No changes to this sprint&rsquo;s scope so far.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {changes.map((change) => (
            <PlanChangeRow key={change.id} workspaceId={workspaceId} change={change} canDecide={canDecide} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RaiseForm({
  workspaceId,
  timeline,
  backlog,
  onDone,
}: {
  workspaceId: string;
  timeline: PlanColumn;
  backlog: readonly BacklogRow[];
  onDone: () => void;
}) {
  const vendorName = useVendorName();
  const queryClient = useQueryClient();
  const [addId, setAddId] = useState('');
  const [removeId, setRemoveId] = useState('');
  const [reason, setReason] = useState('');

  // What could come in: waiting work that has an estimate. An unestimated item
  // has no honest effect on hours to show, and "what it costs" is half the ask.
  const addable = backlog.filter((row) => row.approxHours > 0);
  const removable = timeline.items.filter((item) => item.removedAt === null && item.status !== 'released');

  const add = addable.find((row) => row.id === addId);
  const remove = removable.find((item) => item.requestId === removeId);
  const delta = (add?.approxHours ?? 0) - (remove?.approxHours ?? 0);
  const after = timeline.hours + delta;

  const raise = useMutation({
    mutationFn: () =>
      raisePlanChange(workspaceId, timeline.sprint.id, {
        addRequestId: addId === '' ? null : addId,
        removeRequestId: removeId === '' ? null : removeId,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await invalidatePlanning(queryClient, workspaceId);
      onDone();
    },
  });

  const ready = (addId !== '' || removeId !== '') && reason.trim().length >= 3;

  return (
    <div className="mb-3 flex flex-col gap-3 rounded-xl border border-line p-4 animate-enter">
      <p className="fs-13 text-ink-soft">
        Pick what to bring in and what to take out, and say why. {vendorName} talks it through with you and
        applies the swap once it is agreed. Nothing moves until then.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1 fs-11 text-muted">
          Bring in from the backlog
          <select
            value={addId}
            onChange={(event) => setAddId(event.target.value)}
            className="min-w-0 rounded-md border border-line bg-base px-2 py-1.5 fs-12.5 text-ink"
          >
            <option value="">Nothing</option>
            {addable.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.number} {row.title} · ~{formatHours(row.approxHours)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1 fs-11 text-muted">
          Take out of {timeline.sprint.name}
          <select
            value={removeId}
            onChange={(event) => setRemoveId(event.target.value)}
            className="min-w-0 rounded-md border border-line bg-base px-2 py-1.5 fs-12.5 text-ink"
          >
            <option value="">Nothing — accept the sprint may run over</option>
            {removable.map((item) => (
              <option key={item.requestId} value={item.requestId}>
                #{item.number} {item.title} · ~{formatHours(item.approxHours)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex flex-col gap-1 fs-11 text-muted">
        Why
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          placeholder="What changed on your side?"
          className="resize-none rounded-md border border-line px-2.5 py-2 fs-13 text-ink"
        />
      </label>

      {(add !== undefined || remove !== undefined) && (
        <p className="fs-12 text-muted">
          Effect on the sprint: {delta >= 0 ? '+' : '−'}
          {formatHours(Math.abs(delta))}, to about {formatHours(after)} of{' '}
          {formatHours(timeline.sprint.plannableHours)} plannable.
          {remove === undefined && add !== undefined && ' Nothing comes out, so the sprint may run over.'}
        </p>
      )}

      {raise.error !== null && (
        <p role="alert" className="fs-12 text-danger">
          {raise.error.message}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!ready || raise.isPending}
          onClick={() => raise.mutate()}
        >
          {raise.isPending ? 'Sending…' : `Send to ${vendorName}`}
        </Button>
      </div>
    </div>
  );
}

const STATUS_CHIP: Readonly<Record<PlanChange['status'], { label: string; className: string }>> = {
  open: { label: 'Waiting for Noorix', className: 'bg-warn-tint text-warn' },
  agreed: { label: 'Agreed', className: 'bg-success-tint text-success' },
  declined: { label: 'Declined', className: 'bg-surface-3 text-muted' },
};

function ItemLine({ verb, item, sign }: { verb: string; item: PlanChangeItem; sign: '+' | '−' }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2 fs-13">
      <span className="w-16 flex-none text-muted">{verb}</span>
      {item.requestId !== null ? (
        <Link
          to={`/requests/${item.requestId}`}
          className="min-w-0 truncate font-semibold text-ink hover:text-accent"
        >
          #{item.number} {item.title}
        </Link>
      ) : (
        <span className="min-w-0 truncate font-semibold text-ink">{item.title}</span>
      )}
      <span className="ml-auto flex-none text-muted numeric">
        {sign}
        {formatHours(item.approxHours)}
      </span>
    </div>
  );
}

function PlanChangeRow({
  workspaceId,
  change,
  canDecide,
}: {
  workspaceId: string;
  change: PlanChange;
  canDecide: boolean;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const decide = useMutation({
    mutationFn: (decision: 'agree' | 'decline') =>
      decidePlanChange(workspaceId, change.id, decision, note.trim() === '' ? undefined : note.trim()),
    onSuccess: () => invalidatePlanning(queryClient, workspaceId),
  });
  const chip = STATUS_CHIP[change.status];

  // Arriving from the Review tab's link (#plan-change-<id>): the page renders
  // after its data loads, so the browser's own hash scroll has already missed.
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (window.location.hash === `#plan-change-${change.id}`) {
      ref.current?.scrollIntoView({ block: 'center' });
    }
  }, [change.id]);

  return (
    <li
      ref={ref}
      id={`plan-change-${change.id}`}
      className={cx(
        'rounded-xl border px-4 py-3',
        change.status === 'open' ? 'border-warn-border' : 'border-line',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className={cx('rounded-full px-2 py-0.5 fs-11 font-bold', chip.className)}>{chip.label}</span>
        <span className="fs-11 text-muted">
          {change.raisedByName ?? 'Someone'} · {formatDate(change.raisedAt)}
        </span>
        {change.hoursDelta !== null && (
          <span className="ml-auto fs-11 font-semibold text-ink numeric">
            Effect {change.hoursDelta >= 0 ? '+' : '−'}
            {formatHours(Math.abs(change.hoursDelta))}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {change.add !== null && <ItemLine verb="Bring in" item={change.add} sign="+" />}
        {change.remove !== null && <ItemLine verb="Take out" item={change.remove} sign="−" />}
        {change.add === null && change.remove !== null && (
          <div className="fs-12 text-muted">Nothing brought in.</div>
        )}
        {change.remove === null && change.add !== null && (
          <div className="fs-12 text-muted">Nothing taken out — the sprint may run over.</div>
        )}
      </div>

      <p className="mt-2 border-l-2 border-line-strong pl-2.5 fs-13 text-ink-soft">{change.reason}</p>

      {change.status !== 'open' && (
        <p className="mt-2 fs-12 text-muted">
          {change.status === 'agreed' ? 'Agreed' : 'Declined'} by {change.decidedByName ?? 'Noorix'}
          {change.decidedAt !== null && ` · ${formatDate(change.decidedAt)}`}
          {change.decisionNote !== null && ` — ${change.decisionNote}`}
        </p>
      )}

      {change.status === 'open' && canDecide && (
        <div className="mt-3 flex flex-col gap-2 border-t border-line pt-3">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="What was agreed, in a sentence (shown to the client)"
            className="rounded-md border border-line px-2.5 py-1.5 fs-12.5"
          />
          {decide.error !== null && (
            <p role="alert" className="fs-12 text-danger">
              {decide.error.message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate('decline')}>
              Decline
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={decide.isPending}
              onClick={() => decide.mutate('agree')}
            >
              Agree and apply
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
